// AI image generation event definitions for analytics.
// Tracks the lifecycle of an AI-generated image (started, completed, failed,
// used, retried) for both outcome and cover image surfaces inside the
// proposal/launchpad creation flow.

import type { EventDefinition } from './navigation';

export type ImageGenerationMode =
  | 'outcome'
  | 'cover'
  | 'avatar'
  | 'hero'
  | 'morfian'
  | 'morfianate';

export type ImageGenerationFailureReason =
  | 'rate_limit'
  | 'quota_exceeded'
  | 'model'
  | 'network'
  | 'nsfw'
  | 'unknown';

export type ImageGenerationStartedProperties = {
  mode: ImageGenerationMode;
  hasUserPrompt: boolean;
};

export type ImageGenerationCompletedProperties = {
  mode: ImageGenerationMode;
  latencyMs: number;
  hasUserPrompt: boolean;
};

export type ImageGenerationFailedProperties = {
  mode: ImageGenerationMode;
  reason: ImageGenerationFailureReason;
};

export type ImageGenerationUsedProperties = {
  mode: ImageGenerationMode;
};

export type ImageGenerationRetriedProperties = {
  mode: ImageGenerationMode;
};

export const IMAGE_GENERATION_STARTED: EventDefinition<ImageGenerationStartedProperties> = {
  name: 'image_generation_started',
};

export const IMAGE_GENERATION_COMPLETED: EventDefinition<ImageGenerationCompletedProperties> = {
  name: 'image_generation_completed',
};

export const IMAGE_GENERATION_FAILED: EventDefinition<ImageGenerationFailedProperties> = {
  name: 'image_generation_failed',
};

export const IMAGE_GENERATION_USED: EventDefinition<ImageGenerationUsedProperties> = {
  name: 'image_generation_used',
};

export const IMAGE_GENERATION_RETRIED: EventDefinition<ImageGenerationRetriedProperties> = {
  name: 'image_generation_retried',
};

// Image search (third tab in the launchpad image picker — Serper)

export type ImageSearchProvider = 'serper';
export type ImageSearchFailureReason = 'rate_limit' | 'network' | 'unknown';

export type ImageSearchStartedProperties = {
  mode: ImageGenerationMode;
  queryLength: number;
  autoTriggered: boolean;
};

export type ImageSearchCompletedProperties = {
  mode: ImageGenerationMode;
  provider: ImageSearchProvider;
  resultCount: number;
  latencyMs: number;
};

export type ImageSearchFailedProperties = {
  mode: ImageGenerationMode;
  reason: ImageSearchFailureReason;
};

export type ImageSearchUsedProperties = {
  mode: ImageGenerationMode;
  sourceDomain: string;
};

export const IMAGE_SEARCH_STARTED: EventDefinition<ImageSearchStartedProperties> = {
  name: 'image_search_started',
};

export const IMAGE_SEARCH_COMPLETED: EventDefinition<ImageSearchCompletedProperties> = {
  name: 'image_search_completed',
};

export const IMAGE_SEARCH_FAILED: EventDefinition<ImageSearchFailedProperties> = {
  name: 'image_search_failed',
};

export const IMAGE_SEARCH_USED: EventDefinition<ImageSearchUsedProperties> = {
  name: 'image_search_used',
};
